// Clean, print-based PDF export for Fier reports.
// Opens a new window with a professional print layout and triggers the browser print dialog
// (user chooses "Save as PDF"). No external dependencies.
import { formatCurrency, formatPercent } from './format';

function esc(s) {
  return String(s === null || s === undefined ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function baseStyles() {
  return `
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: 'Figtree', 'Helvetica Neue', Arial, sans-serif;
      color: #1a1f2e; background: #fff;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .page { max-width: 820px; margin: 0 auto; padding: 32px 30px 40px; }
    .doc-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
    .brand .company { font-size: 21px; font-weight: 700; letter-spacing: -0.01em; color: #0f172a; }
    .brand .title { font-size: 13.5px; font-weight: 600; margin-top: 3px; color: #334155; }
    .brand .period { font-size: 12px; color: #64748b; margin-top: 2px; }
    .gen { font-size: 10px; color: #94a3b8; text-align: right; line-height: 1.6; white-space: nowrap; }
    .gen b { color: #475569; font-weight: 600; }
    .rule { border: none; border-top: 2px solid #0f172a; margin: 14px 0 0; }
    table.report { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 9px; }
    table.report thead th {
      text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em;
      color: #64748b; font-weight: 700; padding: 6px 8px; border-bottom: 1.5px solid #cbd5e1;
    }
    table.report tbody td { font-size: 9px; padding: 5px 8px; border-bottom: 1px solid #eef2f6; vertical-align: top; }
    table.report .r { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
    table.report .l { text-align: left; }
    .muted { color: #64748b; }
    .neg { color: #b91c1c; }
    .code { color: #94a3b8; font-size: 9px; margin-right: 5px; font-variant-numeric: tabular-nums; }
    .note { display: block; font-size: 9px; color: #94a3b8; margin-top: 1px; }
    .indent { padding-left: 24px !important; }
    .tag { display: inline-block; font-size: 9px; padding: 1px 6px; border: 1px solid #cbd5e1; border-radius: 999px; color: #475569; margin: 1px 2px 1px 0; }
    .tag-warn { color: #b45309; border-color: #f5d08a; }
    .tag-muted { color: #94a3b8; border-color: #e2e8f0; }
    tr.section td { background: #f1f5f9; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; color: #475569; padding: 5px 8px; }
    tr.total td { font-weight: 700; background: #f8fafc; }
    tr.total-strong td { font-weight: 700; background: #eef2ff; }
    tr.rettifica td { color: #4338ca; background: #f7f6ff; }
    .doc-footer { margin-top: 26px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; text-align: center; }
    @page { size: A4; margin: 14mm; }
    @media print {
      .page { max-width: none; padding: 0; }
      tr.section td, tr.total td, tr.total-strong td, tr.rettifica td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;
}

function openPrint({ company, title, period, tableHtml }) {
  const gen = new Date().toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const html = `<!doctype html>
<html lang="it">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${esc(title)}${period ? ' · ' + esc(period) : ''}</title>
  <style>${baseStyles()}</style>
</head>
<body>
  <div class="page">
    <div class="doc-header">
      <div class="brand">
        <div class="company">${esc(company || 'Fier S.p.A.')}</div>
        <div class="title">${esc(title)}</div>
        ${period ? `<div class="period">${esc(period)}</div>` : ''}
      </div>
      <div class="gen">Generato il<br /><b>${esc(gen)}</b></div>
    </div>
    <hr class="rule" />
    ${tableHtml}
    <div class="doc-footer">Documento generato automaticamente · Fier Holding</div>
  </div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { try { window.focus(); window.print(); } catch (e) {} }, 350);
    });
  </script>
</body>
</html>`;

  const w = window.open('', '_blank');
  if (!w) {
    alert('Abilita i popup per esportare il PDF, poi riprova.');
    return false;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}

/**
 * Export a reclassified income statement (Conto Economico) to a clean PDF.
 * rows: array of { kind: 'section'|'total'|'account'|'rettifica'|undefined, label, code, pct, value, nota, strong }
 * pctFraction: whether pct values are fractions (0..1) or already percentages.
 */
export function exportCEPdf({ company, title, period, rows, pctFraction = false }) {
  const showPct = (rows || []).some((r) => r.pct != null);
  const colSpan = showPct ? 3 : 2;
  const head = `<thead><tr><th class="l">Voce</th>${showPct ? '<th class="r">% ricavi</th>' : ''}<th class="r">Valore</th></tr></thead>`;
  const body = (rows || []).map((r) => {
    if (r.kind === 'section') {
      return `<tr class="section"><td colspan="${colSpan}">${esc(r.label)}</td></tr>`;
    }
    const isTotal = r.kind === 'total';
    const isRett = r.kind === 'rettifica';
    const trClass = isTotal ? (r.strong ? 'total-strong' : 'total') : (isRett ? 'rettifica' : '');
    const indent = r.kind === 'account' || isRett ? ' indent' : '';
    const code = r.code ? `<span class="code">${esc(r.code)}</span>` : '';
    const note = r.nota ? `<span class="note">${esc(r.nota)}</span>` : '';
    const label = `<td class="l${indent}">${code}${esc(r.label)}${note}</td>`;
    const pct = showPct ? `<td class="r muted">${r.pct != null ? formatPercent(r.pct, 1, pctFraction) : ''}</td>` : '';
    const valCls = 'r' + ((r.value || 0) < 0 ? ' neg' : '');
    const val = `<td class="${valCls}">${r.value != null ? formatCurrency(r.value) : ''}</td>`;
    return `<tr class="${trClass}">${label}${pct}${val}</tr>`;
  }).join('');
  const tableHtml = `<table class="report">${head}<tbody>${body}</tbody></table>`;
  return openPrint({ company, title, period, tableHtml });
}

/**
 * Export a generic list table to a clean PDF.
 * columns: [{ label, align: 'left'|'right', render: (row) => string | {html} }]
 * rows: array of objects
 */
export function exportListPdf({ company, title, period, columns, rows }) {
  const head = `<thead><tr>${columns.map((c) => `<th class="${c.align === 'right' ? 'r' : 'l'}">${esc(c.label)}</th>`).join('')}</tr></thead>`;
  const body = (rows || []).map((row) => {
    const cells = columns.map((c) => {
      const out = c.render ? c.render(row) : row[c.key];
      const isHtml = out && typeof out === 'object' && 'html' in out;
      const content = isHtml ? out.html : esc(out);
      return `<td class="${c.align === 'right' ? 'r' : 'l'}">${content}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }).join('');
  const tableHtml = `<table class="report">${head}<tbody>${body}</tbody></table>`;
  return openPrint({ company, title, period, tableHtml });
}
